import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Row, Stack } from "react-bootstrap";
import { useLoaderData, useLocation, useNavigate, useRouteLoaderData } from "react-router-dom";
import { toast } from "react-toastify";
import { axiosInstance } from "../../auth/Auth";
import EmptyRow from "../../components/EmptyRow";
import { getErrMsg } from "../../components/HelperFunctions";
import { useEditableList } from "../../components/HelperHooks";
import LoadingIndicator from "../../components/LoadingIndicator";
import StationPicker from "../../components/Pickers/StationPicker";
import { useUserFormState } from "../../components/UserSelection";
import useSortableData from "../../components/useSortableData";
import RATooltip from "../../components/RATooltip";
import PredictorSettingsHeader from "../../components/predictor/PredictorSettingsHeader";
import PredictorSettingsRow from "../../components/predictor/PredictorSettingsRow";
import PredictorSetupBanner from "../../components/predictor/PredictorSetupBanner";
import PredictorWeightsHeader from "../../components/predictor/PredictorWeightsHeader";
import PredictorWeightRow from "../../components/predictor/PredictorWeightRow";
import { RATable } from "../../components/pageSections";

const blankSettings = radioId => ({
    radio_id: radioId,
    look_back_weeks: 0,
    max_spins: 0,
    max_song_age_weeks: "",
    song_release_start: "",
    song_release_end: "",
});

const normalizeOptionalNumber = value => (value === "" || value == null ? null : Number(value));
const normalizeRequiredNumber = value => Number(value);

function sanitizeSettings(settings) {
    return {
        look_back_weeks: normalizeRequiredNumber(settings.look_back_weeks),
        max_spins: normalizeRequiredNumber(settings.max_spins),
        max_song_age_weeks: normalizeOptionalNumber(settings.max_song_age_weeks),
        song_release_start: normalizeOptionalNumber(settings.song_release_start),
        song_release_end: normalizeOptionalNumber(settings.song_release_end),
    };
}

function isSettingsInvalid(settings) {
    const payload = sanitizeSettings(settings);
    return (
        Number.isNaN(payload.look_back_weeks) ||
        Number.isNaN(payload.max_spins) ||
        payload.look_back_weeks < 0 ||
        payload.max_spins < 0 ||
        (payload.max_song_age_weeks != null && payload.max_song_age_weeks < 0) ||
        (payload.song_release_start != null && payload.song_release_start < 0) ||
        (payload.song_release_end != null && payload.song_release_end < 0) ||
        (payload.song_release_start != null && payload.song_release_end != null && payload.song_release_start > payload.song_release_end)
    );
}

function sanitizeWeight(item) {
    return {
        target_type: item.target_type,
        target_id: Number(item.target_id),
        weight: Number(item.weight),
    };
}

function isWeightInvalid(item, items) {
    const payload = sanitizeWeight(item);
    const duplicate = items.some(x => x.setting_id !== item.setting_id && x.target_type === item.target_type && Number(x.target_id) === payload.target_id);
    return (
        !payload.target_type ||
        !payload.target_id ||
        !payload.weight ||
        Number.isNaN(payload.target_id) ||
        Number.isNaN(payload.weight) ||
        payload.weight <= 0 ||
        duplicate
    );
}

export default function SettingsPredictor() {
    const moduleName = "settingsPredictor";

    const location = useLocation();
    const navigate = useNavigate();
    const { hybridHitlists, digitalHitlists } = useLoaderData();
    const { competitorRadios, radios } = useRouteLoaderData("root");
    const predictorSetupState = location.state?.predictorSetup;
    const predictorSetupRadioId = predictorSetupState?.radioId;
    const predictorSetupOpenSetupForm = Boolean(predictorSetupState?.openSetupForm);

    const [formData, setFormData, saveFormData, , hydrated] = useUserFormState(moduleName);
    const [settings, setSettings] = useState(null);
    const [originalSettings, setOriginalSettings] = useState(null);
    const [settingsEditing, setSettingsEditing] = useState(false);
    const [showSetupForm, setShowSetupForm] = useState(false);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsError, setSettingsError] = useState(null);
    const [weightsLoading, setWeightsLoading] = useState(false);
    const [weightsData, setWeightsData] = useState(null);
    const [recalculating, setRecalculating] = useState(false);
    const [targetOptions, setTargetOptions] = useState({ radio: [], digital_hitlist: [], hybrid_hitlist: [] });
    const [selectedRadioId, setSelectedRadioId] = useState(null);

    const saveFormDataRef = useRef(saveFormData);
    const activeRadioId = formData.station[0]?.radio_id ?? null;

    useEffect(() => {
        saveFormDataRef.current = saveFormData;
    }, [saveFormData]);

    useEffect(() => {
        if (!hydrated || !predictorSetupRadioId) return;

        let cancelled = false;
        (async () => {
            const resolvedRadios = (await radios).data;
            if (cancelled) return;
            const station = resolvedRadios.find(x => x.radio_id === predictorSetupRadioId);
            if (!station) return;
            setFormData(current => (current.station[0]?.radio_id === station.radio_id ? current : { ...current, station: [station] }));
        })();

        return () => (cancelled = true);
    }, [hydrated, predictorSetupRadioId, radios, setFormData]);

    useEffect(() => {
        let cancelled = false;
        Promise.all([competitorRadios, hybridHitlists, digitalHitlists]).then(([resolvedRadios, resolvedHybridHitlists, resolvedDigitalHitlists]) => {
            if (cancelled) return;
            setTargetOptions({
                radio: resolvedRadios.data.map(radio => ({ id: radio.radio_id, label: radio.radio_name })),
                digital_hitlist: resolvedDigitalHitlists.data.map(hitlist => ({ id: hitlist.digital_hitlist_id, label: hitlist.name })),
                hybrid_hitlist: resolvedHybridHitlists.data.map(hitlist => ({ id: hitlist.hybrid_hitlist_id, label: hitlist.name })),
            });
        });
        return () => (cancelled = true);
    }, [competitorRadios, digitalHitlists, hybridHitlists]);

    const loadWeights = useCallback(async (radioId, signal) => {
        setWeightsLoading(true);
        try {
            const { data } = await axiosInstance.get(`/predictor/settings/${radioId}/weights`, { signal });
            if (signal?.aborted) return;
            setWeightsData(data);
        } catch (err) {
            if (signal?.aborted || err?.code === "ERR_CANCELED") return;
            console.error("Failed to load predictor weights", err);
            setWeightsData([]);
        } finally {
            if (!signal?.aborted) setWeightsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!activeRadioId) {
            setSelectedRadioId(null);
            setSettings(null);
            setOriginalSettings(null);
            setWeightsData(null);
            setSettingsError(null);
            setSettingsLoading(false);
            setWeightsLoading(false);
            return;
        }

        const controller = new AbortController();
        const { signal } = controller;
        setSelectedRadioId(activeRadioId);
        setSettingsLoading(true);
        setWeightsLoading(false);
        setSettings(null);
        setOriginalSettings(null);
        setWeightsData(null);
        setSettingsError(null);
        setSettingsEditing(false);
        setShowSetupForm(false);

        (async () => {
            try {
                const { data } = await axiosInstance.get(`/predictor/settings/${activeRadioId}`, { signal });
                if (signal.aborted) return;
                setSettings(data);
                setOriginalSettings(data);
                await saveFormDataRef.current();
                if (signal.aborted) return;
                await loadWeights(activeRadioId, signal);
            } catch (err) {
                if (signal.aborted || err?.code === "ERR_CANCELED") return;
                await saveFormDataRef.current();
                if (signal.aborted) return;
                if (err.response?.status === 404) {
                    setSettingsError(null);
                    if (predictorSetupRadioId === activeRadioId && predictorSetupOpenSetupForm) {
                        setSettings(blankSettings(activeRadioId));
                        setSettingsEditing(true);
                        setShowSetupForm(true);
                    }
                } else {
                    console.error("Failed to load predictor settings", err);
                    setSettingsError(err.response?.status ?? 500);
                }
            } finally {
                if (!signal.aborted) setSettingsLoading(false);
            }
        })();

        return () => controller.abort();
    }, [activeRadioId, loadWeights, predictorSetupOpenSetupForm, predictorSetupRadioId]);

    const handleCreateSettings = () => {
        setSettings(blankSettings(selectedRadioId));
        setSettingsEditing(true);
        setShowSetupForm(true);
    };

    const handleSettingsChange = (key, value) => {
        setSettings(current => ({ ...current, [key]: value }));
    };

    const handleSettingsDiscard = () => {
        if (originalSettings) {
            setSettings(originalSettings);
            setSettingsEditing(false);
        } else {
            setSettings(null);
            setShowSetupForm(false);
        }
    };

    const handleSettingsSave = async () => {
        if (isSettingsInvalid(settings)) return;
        try {
            const { data } = await toast.promise(axiosInstance.put(`/predictor/settings/${selectedRadioId}`, sanitizeSettings(settings)), {
                pending: "Saving predictor settings...",
                success: "Successfully saved predictor settings!",
                error: {
                    render({ data }) {
                        return (
                            <div>
                                <h6>Failed to save predictor settings!</h6>
                                <span className="fs-7 text-muted">{getErrMsg(data)}</span>
                            </div>
                        );
                    },
                },
            });
            setSettings(data);
            setOriginalSettings(data);
            setSettingsEditing(false);
            setShowSetupForm(false);
            await loadWeights(selectedRadioId);
        } catch (err) {
            console.error("Failed to save predictor settings", err);
        }
    };

    const handleRecalculate = async () => {
        setRecalculating(true);
        try {
            await toast.promise(axiosInstance.post(`/predictor/${selectedRadioId}/recalculate`), {
                pending: "Requesting recalculation...",
                success: "Recalculation requested. Results will be available shortly.",
                error: {
                    render({ data }) {
                        return (
                            <div>
                                <h6>Failed to request recalculation!</h6>
                                <span className="fs-7 text-body-secondary">{getErrMsg(data)}</span>
                            </div>
                        );
                    },
                },
            });
        } catch (err) {
            console.error("Failed to request predictor recalculation", err);
        } finally {
            setRecalculating(false);
        }
    };

    const handleGoToResults = () => {
        navigate("/music/predictor", { state: { presetStation: formData.station[0] } });
    };

    const initialWeights = useMemo(() => weightsData ?? [], [weightsData]);

    const { items: weights, handlers } = useEditableList({
        initialItems: initialWeights,
        idKey: "setting_id",
        itemName: "predictor weight",
        createTempItem: curr => ({
            status: "ADD",
            setting_id: -(curr.length + 1),
            radio_id: selectedRadioId,
            target_type: "radio",
            target_id: "",
            weight: 1,
        }),
        createItem: async item => {
            if (isWeightInvalid(item, weights)) throw new Error("Invalid payload");
            const { data } = await axiosInstance.post(`/predictor/settings/${selectedRadioId}/weights`, sanitizeWeight(item));
            return data;
        },
        updateItem: async item => {
            if (isWeightInvalid(item, weights)) throw new Error("Invalid payload");
            const { data } = await axiosInstance.put(`/predictor/settings/weights/${item.setting_id}`, sanitizeWeight(item));
            return data;
        },
        deleteItem: async ({ setting_id }) => await axiosInstance.delete(`/predictor/settings/weights/${setting_id}`),
    });

    const { sortedItems, requestSort, sortConfig } = useSortableData(weights, { key: "setting_id", direction: "ascending" });
    const canRecalculate = originalSettings && !settingsEditing && weights.length > 0 && weights.every(w => !w.status);

    return (
        <Row>
            <Col className="bg-white p-4">
                <h2 className="text-uppercase">Predictor</h2>
                <Row>
                    <StationPicker formData={formData} setFormData={setFormData} size="lg" name={moduleName} moduleFilterFunction={x => x.module_id === 16} />
                    <Col xs={12} xl={"auto"} className="text-center mt-xl-auto ms-xl-auto">
                        <Stack direction="horizontal" gap={2} className="justify-content-center">
                            <Button variant="ra-yellow-outline" className="text-uppercase px-5 py-1" onClick={handleGoToResults} disabled={!selectedRadioId}>
                                Go to Results
                            </Button>
                            <Button
                                variant="ra-yellow"
                                className="text-uppercase px-5 py-1"
                                onClick={handleRecalculate}
                                disabled={recalculating || !canRecalculate}>
                                Recalculate
                            </Button>
                        </Stack>
                    </Col>
                </Row>
                <hr />
                {settingsLoading ? (
                    <LoadingIndicator width={75} />
                ) : settingsError ? (
                    <Alert variant="danger">Failed to load Predictor settings. Please try again.</Alert>
                ) : (
                    selectedRadioId && (
                        <>
                            {!settings && !showSetupForm && <PredictorSetupBanner onButtonClick={handleCreateSettings} />}
                            {settings && (
                                <>
                                    <Stack direction="horizontal" className="mb-2 align-items-center column-gap-1">
                                        <h3 className="text-uppercase h4 mb-0">General Settings</h3>
                                        <RATooltip size={28}>
                                            <p>Choose how to limit which songs Predictor looks at:</p>
                                            <ul>
                                                <li>
                                                    <strong>Look back Weeks</strong> - How far back to check for spins on your own station and predictor
                                                    stations. 8 weeks back is a good starting point, 0 would be all spins ever played on the stations.
                                                </li>
                                                <li>
                                                    <strong>Max Spins</strong> - Number of spins within the look back weeks that a song can have before it is
                                                    excluded from the Predictor.
                                                </li>
                                                <li>
                                                    <strong>Max Song Age / Release Year</strong> - Additional optional filters to limit which songs are chosen.
                                                    Add older release years here to look for back pool songs, a Max Song Age to look for new releases.
                                                </li>
                                            </ul>
                                        </RATooltip>
                                    </Stack>
                                    <div className="overflow-x-auto">
                                        <PredictorSettingsHeader minWidth={600} />
                                        <PredictorSettingsRow
                                            settings={settings}
                                            editing={settingsEditing}
                                            invalid={isSettingsInvalid(settings)}
                                            minWidth={600}
                                            onChange={handleSettingsChange}
                                            onEdit={() => setSettingsEditing(true)}
                                            onSave={handleSettingsSave}
                                            onDiscard={handleSettingsDiscard}
                                        />
                                    </div>
                                </>
                            )}
                            {originalSettings && (
                                <>
                                    <hr />
                                    <Stack direction="horizontal" className="mb-2 align-items-center column-gap-1">
                                        <h3 className="text-uppercase h4 mb-0">Sources and Weights</h3>
                                        <RATooltip size={28}>
                                            <p>Configures the sources used to rank songs for your station:</p>
                                            <ul>
                                                <li>
                                                    <strong>Weight</strong> - How much influence a source has. Higher = more influence
                                                </li>
                                                <li>
                                                    <strong>Type</strong> - The station or list type. Radio station, digital or hybrid hitlist
                                                </li>
                                            </ul>
                                        </RATooltip>
                                        <Button variant="ra-yellow" size="sm" className="ms-auto" onClick={() => handlers.add()}>
                                            Create New
                                        </Button>
                                    </Stack>
                                    <div className="overflow-x-auto">
                                        <RATable>
                                            <RATable.Header>
                                                <PredictorWeightsHeader sortConfig={sortConfig} requestSort={requestSort} minWidth={600} />
                                            </RATable.Header>
                                            <RATable.Body>
                                                {weightsLoading ? (
                                                    <LoadingIndicator width={75} />
                                                ) : (
                                                    <>
                                                        {!sortedItems?.length ? (
                                                            <EmptyRow text="No Predictor weights" />
                                                        ) : (
                                                            sortedItems.map(x => (
                                                                <PredictorWeightRow
                                                                    key={x.setting_id}
                                                                    item={x}
                                                                    targetOptions={targetOptions}
                                                                    invalid={isWeightInvalid(x, weights)}
                                                                    existingStations={weights.filter(w => w.target_type === "radio").map(w => w.target_id)}
                                                                    existingHybridHitlists={weights
                                                                        .filter(w => w.target_type === "hybrid_hitlist")
                                                                        .map(w => w.target_id)}
                                                                    existingDigitalHitlists={weights
                                                                        .filter(w => w.target_type === "digital_hitlist")
                                                                        .map(w => w.target_id)}
                                                                    dataHandlers={handlers}
                                                                />
                                                            ))
                                                        )}
                                                    </>
                                                )}
                                            </RATable.Body>
                                        </RATable>
                                    </div>
                                </>
                            )}
                        </>
                    )
                )}
            </Col>
        </Row>
    );
}
